import { AlertTriangle, Lightbulb, ThumbsUp } from "lucide-react";
import type { ReactNode } from "react";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import type { Summary } from "../lib/stats";
import { Card, Pnl } from "./ui";

export interface Insight { tone: "good" | "bad" | "info"; text: ReactNode }

/** Les constats qui comptent, en tête de chaque catégorie. */
export function Insights({ items }: { items: Insight[] }) {
  const t = useTranslation().lite;
  if (!items.length) return null;
  const icon = { good: <ThumbsUp size={16} />, bad: <AlertTriangle size={16} />, info: <Lightbulb size={16} /> };
  const cls = { good: "bg-pnlPositive/10 text-pnlPositive", bad: "bg-pnlNegative/10 text-pnlNegative", info: "bg-primary/10 text-primary" };
  return (
    <section className="bg-card border border-border rounded-2xl p-5">
      <h2 className="text-sm font-semibold mb-3">{t.blocks.takeaways}</h2>
      <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-3">
        {items.map((it, i) => (
          <div key={i} className="flex gap-3 rounded-xl border border-border p-3.5">
            <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${cls[it.tone]}`}>{icon[it.tone]}</span>
            <p className="text-sm leading-relaxed text-foreground/90">{it.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export interface Metric { label: string; value: ReactNode; hint?: ReactNode; tone?: "gain" | "loss" }

export function MetricGrid({ metrics, title, subtitle }: { metrics: Metric[]; title?: string; subtitle?: string }) {
  return (
    <Card title={title} subtitle={subtitle}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-x-6 gap-y-5">
        {metrics.map(m => (
          <div key={m.label} className="min-w-0">
            <div className="text-xs text-muted-foreground truncate">{m.label}</div>
            <div className={`font-mono tabular-nums text-lg font-semibold mt-1 truncate ${m.tone === "gain" ? "text-pnlPositive" : m.tone === "loss" ? "text-pnlNegative" : ""}`}>{m.value}</div>
            {m.hint && <div className="text-[11px] text-muted-foreground/70 mt-0.5 truncate">{m.hint}</div>}
          </div>
        ))}
      </div>
    </Card>
  );
}

export interface BucketRow { key: string; label: ReactNode; s: Summary }

/** Tableau de répartition avec barre de résultat intégrée. */
export function BucketTable({ title, subtitle, rows, className = "" }: { title: string; subtitle?: string; rows: BucketRow[]; className?: string }) {
  const t = useTranslation().lite;
  const { fmt } = useLiteData();
  const maxAbs = Math.max(1, ...rows.map(r => Math.abs(r.s.netPnl)));
  return (
    <Card title={title} subtitle={subtitle} pad={false} className={className}>
      <div className="overflow-x-auto mt-2">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-[11px] text-muted-foreground/70 border-y border-border">
              <th className="text-left font-normal pl-5 py-2" />
              <th className="text-right font-normal px-3 py-2">{t.blocks.trades}</th>
              <th className="text-right font-normal px-3 py-2">{t.blocks.winners}</th>
              <th className="text-right font-normal px-3 py-2">{t.blocks.pf}</th>
              <th className="text-right font-normal px-3 py-2">{t.blocks.average}</th>
              <th className="font-normal px-3 py-2 w-[22%]" />
              <th className="text-right font-normal pr-5 py-2">{t.blocks.result}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.key}>
                <td className="pl-5 py-2.5 whitespace-nowrap">{r.label}</td>
                <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{r.s.count}</td>
                <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{fmt.pct(r.s.winRate)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{fmt.num(r.s.profitFactor)}</td>
                <td className="px-3 py-2.5 text-right"><Pnl value={r.s.expectancy} className="text-xs" /></td>
                <td className="px-3 py-2.5">
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${r.s.netPnl >= 0 ? "bg-pnlPositive" : "bg-pnlNegative"}`} style={{ width: `${(Math.abs(r.s.netPnl) / maxAbs) * 100}%` }} />
                  </div>
                </td>
                <td className="pr-5 py-2.5 text-right"><Pnl value={r.s.netPnl} className="font-medium" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Comparaison de deux groupes de trades (ex. après une perte vs le reste). */
export function Compare({ title, question, a, b, aLabel, bLabel, verdict }: {
  title: string; question: string; a: Summary; b: Summary; aLabel: string; bLabel: string; verdict?: Insight;
}) {
  const t = useTranslation().lite;
  const { fmt } = useLiteData();
  const Col = ({ label, s }: { label: string; s: Summary }) => (
    <div className="rounded-xl bg-muted/60 p-3.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono text-xl font-semibold mt-1">{s.count ? fmt.pct(s.winRate) : "-"}</div>
      <div className="text-[11px] text-muted-foreground/70">{t.blocks.winnersAndTrades(s.count)}</div>
      <div className="mt-2 text-xs">{t.blocks.average} <Pnl value={s.expectancy} /></div>
      <div className="text-xs">{t.blocks.total} <Pnl value={s.netPnl} /></div>
    </div>
  );
  return (
    <Card title={title} subtitle={question}>
      <div className="grid grid-cols-2 gap-3">
        <Col label={aLabel} s={a} />
        <Col label={bLabel} s={b} />
      </div>
      {verdict && <p className={`text-xs mt-3 ${verdict.tone === "bad" ? "text-pnlNegative" : verdict.tone === "good" ? "text-pnlPositive" : "text-muted-foreground"}`}>{verdict.text}</p>}
    </Card>
  );
}
